import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import App from './App.vue'

// 全站暗黑主题（科技黑蓝绿白）
document.documentElement.classList.add('dark')

// 品牌标题（全站生效）
document.title = '找客户·AI客源引擎 —— 客源有路，增长有数'

// Import views
import Dashboard from './views/Dashboard.vue'
import Accounts from './views/Accounts.vue'
import Tasks from './views/Tasks.vue'
import Leads from './views/Leads.vue'
import Settings from './views/Settings.vue'
import Monitor from './views/Monitor.vue'
import ScriptManager from './views/ScriptManager.vue'
import LiveMonitor from './views/LiveMonitor.vue'
import IndustrySetup from './views/IndustrySetup.vue'
import ContentFactory from './views/ContentFactory.vue'
import PublishQueue from './views/PublishQueue.vue'
import VideoGen from './views/VideoGen.vue'
import ReplicaStudio from './views/ReplicaStudio.vue'
import Login from './views/Login.vue'
import Merchants from './views/Merchants.vue'
import Recharge from './views/Recharge.vue'
import MarketingGuru from './views/MarketingGuru.vue'
import LandingPage from './views/LandingPage.vue'
import Benchmark from './views/Benchmark.vue'
import QuickPublish from './views/QuickPublish.vue'
import ContentLibrary from './views/ContentLibrary.vue'
import Help from './views/Help.vue'
import Withdraw from './views/Withdraw.vue'
import Invite from './views/Invite.vue'
import Radar from './views/Radar.vue'

const routes = [
  { path: '/', redirect: '/dashboard' },
  { path: '/dashboard', component: Dashboard, name: 'Dashboard' },
  { path: '/accounts', component: Accounts, name: 'Accounts' },
  { path: '/tasks', component: Tasks, name: 'Tasks' },
  { path: '/leads', component: Leads, name: 'Leads' },
  // ---- 功能合并重定向（2026-08-31）：旧独立页 → 线索库 Tab ----
  { path: '/intent', redirect: '/leads?tab=intent' },
  { path: '/ledger', redirect: '/leads?tab=ledger' },
  { path: '/manual-add', redirect: '/leads?tab=manual' },
  { path: '/calendar', redirect: '/leads?tab=calendar' },
  { path: '/private', redirect: '/leads?tab=private' },
  { path: '/monitor', component: Monitor, name: 'Monitor' },
  { path: '/scripts', component: ScriptManager, name: 'ScriptManager' },
  { path: '/live', component: LiveMonitor, name: 'LiveMonitor' },
  { path: '/settings', component: Settings, name: 'Settings' },
  { path: '/industry', component: IndustrySetup, name: 'IndustrySetup' },
  { path: '/content', component: ContentFactory, name: 'ContentFactory' },
  { path: '/video', component: VideoGen, name: 'VideoGen' },
  { path: '/replica', component: ReplicaStudio, name: 'ReplicaStudio' },
  { path: '/login', component: Login, name: 'Login' },
  { path: '/onboard', redirect: '/login' },   // 门禁三步已并入登入页（2026-09-06）
  { path: '/merchants', component: Merchants, name: 'Merchants' },
  { path: '/recharge', component: Recharge, name: 'Recharge' },
  { path: '/publish', component: PublishQueue, name: 'PublishQueue' },
  { path: '/quick', component: QuickPublish, name: 'QuickPublish' },
  { path: '/marketing', component: MarketingGuru, name: 'MarketingGuru' },
  { path: '/landing', component: LandingPage, name: 'LandingPage' },
  { path: '/benchmark', component: Benchmark, name: 'Benchmark' },
  { path: '/library', component: ContentLibrary, name: 'ContentLibrary' },
  { path: '/help', component: Help, name: 'Help' },
  { path: '/withdraw', component: Withdraw, name: 'Withdraw' },
  { path: '/invite', component: Invite, name: 'Invite' },
  { path: '/radar', component: Radar, name: 'Radar' },
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// ===== 路由守卫：登入门禁（2026-09-06 批次 13）=====
// 登入页内一步到位：登录/注册 → 绑定抖音号 → 选定行业（同一页面切换，不跳页）
// 老用户（已登录且已完成开通）一律直接放行进后台；开通未完成的新用户锁在 /login 内
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('api_token')
  const needOnboard = localStorage.getItem('need_onboard') === '1'

  if (to.path === '/login') {
    // 已登录且完成开通（老用户）→ 直接进后台，不重复看登入页
    if (token && !needOnboard) return next('/dashboard')
    return next()                              // 未登录 / 开通未完成 → 留在登入页
  }
  // 其余后台页：未登录或开通未完成，一律拦回登入专题页
  if (!token) return next('/login')
  if (needOnboard) return next('/login')
  // 🛡 商户管理：仅平台管理员可访问（2026-09-06 批次 17）
  //    菜单已按 role 隐藏，这里再拦一道直连 URL 的访问；后端 /users 接口也做二次校验。
  if (to.path === '/merchants') {
    let role = ''
    try { role = (JSON.parse(localStorage.getItem('current_user') || '{}').role) || '' } catch (e) { role = '' }
    if (role !== 'admin') {
      console.warn('[guard] 商户管理页仅平台管理员可访问，已拦截')
      return next('/dashboard')
    }
  }
  next()
})

const app = createApp(App)
app.use(router)
app.use(ElementPlus, { locale: zhCn })   // 2026-09-03: 全站中文（定时日历等组件不再英文）
app.mount('#app')

// 2026-09-08 批次 30：Element Plus 日期/下拉组件偶发 ResizeObserver loop 警告
// （浏览器无实质影响，dev overlay 会把它渲染成红色报错吓到用户 → 静默忽略该特定错误）
window.addEventListener('error', (e) => {
  const msg = (e && e.message) || ''
  if (msg.indexOf('ResizeObserver loop') !== -1 ||
      msg.indexOf('ResizeObserver') !== -1 && msg.indexOf('loop') !== -1) {
    e.stopImmediatePropagation && e.stopImmediatePropagation()
  }
})

// PWA Service Worker（仅生产环境注册，避免干扰开发热更新）
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('SW 注册失败（不影响使用）:', e)
    })
  })
}
